import angular from "angular";
import { Brand, Calculation, Translations } from "@salaxy/core";
import { Chart, registerables } from "chart.js";
import type { ChartType } from "chart.js";
/** Register chart registerables (ex. tooltip) */
Chart.register(...registerables);

/**
 * Controller for employer and worker charts using chart.js
 */
export class CalcChartController implements angular.IController {
  /**
   * For NG-dependency injection
   * @ignore
   */
  public static $inject = ["$element", "$timeout", "$scope"];

  /** Role for which the chart is rendered: employer (default) or worker */
  public role: "employer" | "worker";

  /** Calculation that is rendered. */
  public calc: Calculation;

  /** Y axis scale */
  public scaleYAxis: boolean;

  /** Chart type: "pie" and "bar" (default) are currently supported. */
  public chartType: "pie" | "bar";

  private chart: Chart;

  /** Created datasets from Calculation input */
  private datasetData: any = {};

  private barChartOptions: any = {};

  private pieChartOptions: any = {};

  private chartColors: any = {};

  constructor(
    private $element: JQLite,
    private $timeout: angular.ITimeoutService,
    private $scope: angular.IScope,
  ) {}

  /** Initializes the controller. */
  public init() {
    this.role = this.role || "employer";
    this.chartType = this.chartType || "bar";
    this.setChartOptions();
    this.setChartColors();
    this.$timeout(() => {
      this.createChart();
    });
    this.$scope.$watch(() => ( { chartType: this.chartType, result: this.calc?.result } ), (newValue) => {
      if (newValue) {

        // update chart does not work, but renders columns instead of pie.
        // this.updateChart();
        if (this.chart) {
          this.chart.destroy();
          this.chart = null;
        }
        this.createChart();
      }
    }, true);
  }

  /** Creates chart based on data and options to canvas or updates chart if it already exists */
  public createChart(): void {
    this.datasetData = this.getDatasetData;
    if (this.scaleYAxis) {
      if (
        this.datasetData.workerChartScale > this.datasetData.employerChartScale
      ) {
        this.setChartYAxisLimit(this.datasetData.workerChartScale);
      } else {
        this.setChartYAxisLimit(this.datasetData.employerChartScale);
      }
    } else {
      if (this.role === "worker") {
        this.setChartYAxisLimit(this.datasetData.workerChartScale);
      } else {
        this.setChartYAxisLimit(this.datasetData.employerChartScale);
      }
    }
    const data = this.getChartData();
    const options = this.chartType === "bar" ? this.barChartOptions : this.pieChartOptions;
    if (!this.chart) {
      const ctx = (this.$element.find("canvas")[0] as HTMLCanvasElement)?.getContext("2d");
      if (ctx) {
        this.chart = new Chart(ctx, {
          type: this.chartType as ChartType,
          data,
          options,
        });
      }
    }
  }

  /**
   * Updates provided current chart
   * "none" parameters means no animation for update
   */
  public updateChart(): void {
    if(this.chart) {
      this.datasetData = this.getDatasetData;
      if (this.scaleYAxis) {
        if (
          this.datasetData.workerChartScale > this.datasetData.employerChartScale
        ) {
          this.setChartYAxisLimit(this.datasetData.workerChartScale);
        } else {
          this.setChartYAxisLimit(this.datasetData.employerChartScale);
        }
      } else {
        if (this.role === "worker") {
          this.setChartYAxisLimit(this.datasetData.workerChartScale);
        } else {
          this.setChartYAxisLimit(this.datasetData.employerChartScale);
        }
      }
      const data = this.getChartData();
      const options = this.chartType === "bar" ? this.barChartOptions : this.pieChartOptions;
      this.chart.data = data;
      this.chart.options = options;
      this.chart.update("none");
    }
  }

  /**
   * Calculates and sets a proper Y axis max value for a given value
   * @param value The number of the highest bar in chart
   */
  private setChartYAxisLimit(value: number) {
    const log = Math.round(Math.log10(value));
    const times = Math.ceil(value / Math.pow(10, log - 1));
    const newValue = Math.pow(10, log - 1) * times;
    this.barChartOptions.scales.y = {
      stacked: true,
      min: 0,
      max: newValue,
      ticks: {
        callback: function(val) {
          return Number.isInteger(val) ? val : null;
        }
      }
    };
  }

  private getChartData(): { labels: string[]; datasets: any[] } {
    switch (this.role) {
      case "worker":
        return {
          labels: [
            `${Translations.get(
              "SALAXY.NG1.CalcChartComponent.createChartWorker.seriesLabel.salaryPayment"
            )} ${this.formatCurrency(this.datasetData.salaryPayment)}`,
            `${Translations.get(
              "SALAXY.NG1.CalcChartComponent.createChartWorker.seriesLabel.tax"
            )} ${this.formatCurrency(this.datasetData.tax)}`,
            `${Translations.get(
              "SALAXY.NG1.CalcChartComponent.createChartWorker.seriesLabel.workerSideCosts"
            )} ${this.formatCurrency(this.datasetData.workerSideCosts)}`,
            `${Translations.get(
              "SALAXY.NG1.CalcChartComponent.createChartWorker.seriesLabel.totalExpenses"
            )} ${this.formatCurrency(
              this.datasetData.benefits + this.datasetData.totalExpenses
            )}`,
          ],
          datasets: this.getDatasets(),
        };
      case "employer":
        return {
          labels: [
            `${Translations.get(
              "SALAXY.NG1.CalcChartComponent.createChartEmployer.seriesLabel.totalGrossSalary"
            )} ${this.formatCurrency(this.datasetData.totalGrossSalary)}`,
            `${Translations.get(
              "SALAXY.NG1.CalcChartComponent.createChartEmployer.seriesLabel.salaryAdditions"
            )} ${this.formatCurrency(this.datasetData.salaryAdditions)}`,
            `${Translations.get(
              "SALAXY.NG1.CalcChartComponent.createChartEmployer.seriesLabel.allSideCosts"
            )} ${this.formatCurrency(this.datasetData.allSideCosts)}`,
            `${Translations.get(
              "SALAXY.NG1.CalcChartComponent.createChartEmployer.seriesLabel.totalExpenses"
            )} ${this.formatCurrency(this.datasetData.totalExpenses)}`,
          ],
          datasets: this.getDatasets(),
        };
      default:
        return {
          labels: [],
          datasets: [],
        };
    }
  }

  private formatCurrency(value: number) {
    const formatter = new Intl.NumberFormat("fi-FI", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
    });
    return formatter.format(value);
  }
  private setChartOptions(): any {
    this.barChartOptions = {
      label: "",
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          stacked: true,
          min: 0,
          max: 0,
          display: false,
        },
        y: {
          stacked: true,
          min: 0,
          max: 5000,
          ticks: {
            callback: function(val) {
              return Number.isInteger(val) ? val : null;
            }
          }
        },
      },
      plugins: {
        datalabels: {
          display: false,
        },
        legend: {
          display: false,
        },
        tooltip: {
          mode: "index",
          callbacks: {
            title() {
              return "";
            },
            label(context: any) {
              return context.dataset.label ?? "";
            },
          },
          itemSort(a: { datasetIndex: number }, b: { datasetIndex: number }) {
            return b.datasetIndex - a.datasetIndex;
          },
        },
      },
      events: ["mousemove", "click"],
    };

    this.pieChartOptions = {
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          mode: "dataset",
          callbacks: {
            title() {
              return "";
            },
            label(context: any) {
              return context.label ?? "";
            },
          },
          itemSort(a: { datasetIndex: number }, b: { datasetIndex: number }) {
            return b.datasetIndex - a.datasetIndex;
          },
        },
      },
      events: ["mousemove", "click"],
    };
  }

  private getDatasets(): any[] {
    if (this.chartType === "pie") {
      switch (this.role) {
        case "worker":
          return [
            {
              label: "",
              data: [
                this.datasetData.salaryPayment,
                this.datasetData.tax,
                this.datasetData.workerSideCosts,
                this.datasetData.benefits + this.datasetData.totalExpenses,
              ],
              backgroundColor: [
                this.chartColors.info,
                this.chartColors.success,
                this.chartColors.warning,
                this.chartColors.danger,
              ],
            },
          ];
        case "employer":
          return [
            {
              label: "",
              data: [
                this.datasetData.totalGrossSalary,
                this.datasetData.salaryAdditions,
                this.datasetData.allSideCosts,
                this.datasetData.totalExpenses,
              ],
              backgroundColor: [
                this.chartColors.info,
                this.chartColors.success,
                this.chartColors.warning,
                this.chartColors.danger,
              ],
            },
          ];
        default:
          return [];
      }
    } else {
      switch (this.role) {
        case "worker":
          return [
            {
              label:
                Translations.get(
                  "SALAXY.NG1.CalcChartComponent.createChartWorker.seriesLabel.salaryPayment"
                ) + this.formatCurrency(this.datasetData.salaryPayment),
              data: [this.datasetData.salaryPayment],
              backgroundColor: this.chartColors.info,
            },
            {
              label:
                Translations.get(
                  "SALAXY.NG1.CalcChartComponent.createChartWorker.seriesLabel.tax"
                ) + this.formatCurrency(this.datasetData.tax),
              data: [this.datasetData.tax],
              backgroundColor: this.chartColors.success,
            },
            {
              label:
                Translations.get(
                  "SALAXY.NG1.CalcChartComponent.createChartWorker.seriesLabel.workerSideCosts"
                ) + this.formatCurrency(this.datasetData.workerSideCosts),
              data: [this.datasetData.workerSideCosts],
              backgroundColor: this.chartColors.warning,
            },
            {
              label:
                Translations.get(
                  "SALAXY.NG1.CalcChartComponent.createChartWorker.seriesLabel.totalExpenses"
                ) +
                this.formatCurrency(
                  this.datasetData.benefits + this.datasetData.totalExpenses
                ),
              data: [
                this.datasetData.benefits + this.datasetData.totalExpenses,
              ],
              backgroundColor: this.chartColors.danger,
            },
          ];
        case "employer":
          return [
            {
              label:
                Translations.get(
                  "SALAXY.NG1.CalcChartComponent.createChartEmployer.seriesLabel.totalGrossSalary"
                ) + this.formatCurrency(this.datasetData.totalGrossSalary),
              data: [this.datasetData.totalGrossSalary],
              backgroundColor: this.chartColors.info,
            },
            {
              label:
                Translations.get(
                  "SALAXY.NG1.CalcChartComponent.createChartEmployer.seriesLabel.salaryAdditions"
                ) + this.formatCurrency(this.datasetData.salaryAdditions),
              data: [this.datasetData.salaryAdditions],
              backgroundColor: this.chartColors.success,
            },
            {
              label:
                Translations.get(
                  "SALAXY.NG1.CalcChartComponent.createChartEmployer.seriesLabel.allSideCosts"
                ) + this.formatCurrency(this.datasetData.allSideCosts),
              data: [this.datasetData.allSideCosts],
              backgroundColor: this.chartColors.warning,
            },
            {
              label:
                Translations.get(
                  "SALAXY.NG1.CalcChartComponent.createChartEmployer.seriesLabel.totalExpenses"
                ) + this.formatCurrency(this.datasetData.totalExpenses),
              data: [this.datasetData.totalExpenses],
              backgroundColor: this.chartColors.danger,
            },
          ];

        default:
          return [];
      }
    }
  }

  private setChartColors() {
    const getColorSet = (code: string) =>
      Brand.getBrandColor(code as any, "rgba");
    this.chartColors = {
      info: getColorSet("info"),
      success: getColorSet("success"),
      warning: getColorSet("warning"),
      danger: getColorSet("danger"),
    };
  }

  private get getDatasetData(): any {
    const calc: Calculation = this.calc;
    if (!calc || !calc.result) {
      return {};
    }

    const { result } = calc;
    const data = {
      // Employer numbers
      totalGrossSalary: result.totals.totalBaseSalary,
      salaryAdditions:
        result.totals.totalGrossSalary - result.totals?.totalBaseSalary,
      allSideCosts: result.employerCalc.allSideCosts,
      finalCost: result.employerCalc.finalCost,

      // Worker numbers
      salaryPayment: result.workerCalc.salaryPayment,
      tax: result.workerCalc.tax,
      workerSideCosts: result.workerCalc.workerSideCosts,
      benefits: result.workerCalc.benefits,
      totalExpenses: result.totals.totalExpenses,
      totalWorkerPayment: result.workerCalc.totalWorkerPayment,

      // Chart scales
      employerChartScale: 0,
      workerChartScale: 0,
    };

    // Calculating max y axis limits
    data.employerChartScale =
      data.totalGrossSalary +
      data.salaryAdditions +
      data.allSideCosts +
      data.totalExpenses;
    data.workerChartScale =
      data.salaryPayment +
      data.tax +
      data.workerSideCosts +
      data.benefits +
      data.totalExpenses;
    return data;
  }
}
